import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext(null);
const POLL_INTERVAL_MS = 10000;

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [newSinceLastPoll, setNewSinceLastPoll] = useState([]);

  // Stable refs that survive re-renders
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const userIdRef = useRef(null);

  const isPollingRole = !!user && (user.role === "client" || user.role === "admin");

  // Reset state when user identity changes (login/logout)
  useEffect(() => {
    const newUserId = user?.id || null;
    if (newUserId !== userIdRef.current) {
      userIdRef.current = newUserId;
      seenIdsRef.current = new Set();
      initializedRef.current = false;
      setEvents([]);
      setNewSinceLastPoll([]);
    }
  }, [user]);

  const refresh = useCallback(async () => {
    if (!isPollingRole) return;
    try {
      const { data } = await api.get("/notifications/mine");
      const list = data || [];

      if (!initializedRef.current) {
        // First poll for this user — seed seenIds with everything, no banners
        list.forEach((e) => seenIdsRef.current.add(e.id));
        initializedRef.current = true;
        setEvents(list);
        // eslint-disable-next-line no-console
        console.log("[Phantasm] notifications first poll, seeded", list.length, "ids");
        return;
      }

      const fresh = list.filter((e) => !seenIdsRef.current.has(e.id));
      fresh.forEach((e) => seenIdsRef.current.add(e.id));
      setEvents(list);

      if (fresh.length > 0) {
        // eslint-disable-next-line no-console
        console.log("[Phantasm] notifications poll: NEW events", fresh.length, fresh.map((f) => f.to_status));
        setNewSinceLastPoll((prev) => [...fresh, ...prev]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[Phantasm] notifications poll failed", err?.message);
    }
  }, [isPollingRole]);

  // Polling loop
  useEffect(() => {
    if (!isPollingRole) return;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isPollingRole, refresh]);

  const markRead = useCallback(async (eventId) => {
    // Optimistic
    setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, read: true } : e)));
    setNewSinceLastPoll((prev) => prev.filter((e) => e.id !== eventId));
    try {
      await api.patch(`/notifications/${eventId}/read`);
    } catch {/* */}
  }, []);

  const markAllRead = useCallback(async () => {
    // Optimistic — flip everything locally
    setEvents((prev) => prev.map((e) => ({ ...e, read: true })));
    setNewSinceLastPoll([]);
    try {
      await api.post("/notifications/mark-all-read");
      // Re-fetch to confirm server state matches
      await refresh();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[Phantasm] mark-all-read failed", err?.message);
    }
  }, [refresh]);

  const dismissBanner = useCallback((eventId) => {
    setNewSinceLastPoll((prev) => prev.filter((e) => e.id !== eventId));
  }, []);

  const unreadCount = useMemo(
    () => events.filter((e) => !e.read).length,
    [events]
  );

  return (
    <NotificationsContext.Provider
      value={{
        events,
        unreadCount,
        newSinceLastPoll,
        refresh,
        markRead,
        markAllRead,
        dismissBanner,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used inside NotificationsProvider");
  return ctx;
}
